import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../../lib/api';
import { Link } from 'react-router-dom';
import { useState } from 'react';

interface DashboardCounts {
  counts: { users: number; teams: number; competencies: number; audit_entries: number };
}

export function AdminDashboardPage() {
  const { data, isLoading, error } = useQuery<DashboardCounts>({
    queryKey: ['admin-dashboard'],
    queryFn: () => api('/api/admin/dashboard'),
  });

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Admin</h1>
          <p className="text-ink-muted">Foundations in place. Integrations and game mechanics land in M1–M4.</p>
        </div>
      </div>

      {isLoading ? <p className="text-ink-muted">Loading…</p> : null}
      {error ? <p className="text-accent-danger">Failed to load dashboard.</p> : null}

      {data ? (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Stat label="Users" value={data.counts.users} to="/admin/users" />
            <Stat label="Teams" value={data.counts.teams} to="/admin/teams" />
            <Stat label="Competencies" value={data.counts.competencies} to="/admin/competencies" />
            <Stat label="Audit entries" value={data.counts.audit_entries} to="/admin/audit" />
          </div>

          <DataMaintenanceCard />
        </>
      ) : null}
    </div>
  );
}

function Stat({ label, value, to }: { label: string; value: number; to: string }) {
  return (
    <Link to={to} className="card block transition-colors hover:bg-bg-elevated">
      <p className="label">{label}</p>
      <p className="mt-1 text-2xl font-semibold">{value}</p>
    </Link>
  );
}

// Admin "fix my data" controls. All three are destructive or
// expensive (well, the first two are; re-evaluate is read-heavy),
// so each shows a confirmation + the result. Audit-logged on the
// server side. Available to admins only.
function DataMaintenanceCard() {
  const qc = useQueryClient();
  const [out, setOut] = useState<{ kind: string; payload: unknown } | null>(null);

  const run = useMutation({
    mutationFn: async (kind: 'reconcile' | 'cleanup' | 're-evaluate' | 'reconcile-per-comp' | 'seed-rules') => {
      const path = kind === 'reconcile'
        ? '/api/admin/reconcile-everything'
        : kind === 'cleanup'
          ? '/api/admin/cleanup-duplicate-xp-awards'
          : kind === 'reconcile-per-comp'
            ? '/api/admin/reconcile-per-competency-progress'
            : kind === 'seed-rules'
              ? '/api/admin/seed-missing-scoring-rules'
              : '/api/admin/re-evaluate-user-state';
      return api<{ ok: boolean; result: unknown }>(path, { method: 'POST' });
    },
    onSuccess: (d, kind) => {
      setOut({ kind, payload: d.result });
      qc.invalidateQueries({ queryKey: ['admin-dashboard'] });
    },
    onError: (e) => setOut({ kind: 'error', payload: e instanceof ApiError ? e.message : 'Request failed.' }),
  });

  return (
    <div className="card space-y-3">
      <div>
        <h2 className="text-sm font-semibold uppercase tracking-wide text-ink-muted">Data maintenance</h2>
        <p className="text-xs text-ink-muted">
          Recovery actions for when the scoring pipeline gets out of sync. Each one is idempotent
          and audit-logged. The combined "Reconcile everything" runs backfill → cleanup duplicates
          → re-evaluate state in that order.
        </p>
      </div>
      <div className="flex flex-wrap gap-2">
        <button
          className="btn"
          onClick={() => {
            if (window.confirm('Seed default scoring rules for any team that has none? Idempotent: teams with rules already are skipped.')) {
              run.mutate('seed-rules');
            }
          }}
          disabled={run.isPending}
        >
          {run.isPending && run.variables === 'seed-rules' ? 'Working…' : 'Seed missing rules'}
        </button>
        <button
          className="btn-primary"
          onClick={() => {
            if (window.confirm('Run reconcile-everything? This re-runs the backfill, removes duplicate awards from the pre-dedup era, and re-evaluates streaks/badges/classes. Safe to re-run.')) {
              run.mutate('reconcile');
            }
          }}
          disabled={run.isPending}
        >
          {run.isPending && run.variables === 'reconcile' ? 'Working…' : 'Reconcile everything'}
        </button>
        <button
          className="btn"
          onClick={() => {
            if (window.confirm('Remove duplicate xp_award rows where the same (user, type, target) fired within 60s? This is destructive (deletes award rows, refunds XP).')) {
              run.mutate('cleanup');
            }
          }}
          disabled={run.isPending}
        >
          {run.isPending && run.variables === 'cleanup' ? 'Working…' : 'Cleanup duplicate awards'}
        </button>
        <button
          className="btn"
          onClick={() => run.mutate('reconcile-per-comp')}
          disabled={run.isPending}
        >
          {run.isPending && run.variables === 'reconcile-per-comp' ? 'Working…' : 'Re-derive per-competency'}
        </button>
        <button
          className="btn"
          onClick={() => run.mutate('re-evaluate')}
          disabled={run.isPending}
        >
          {run.isPending && run.variables === 're-evaluate' ? 'Working…' : 'Re-evaluate state'}
        </button>
      </div>
      {out ? (
        <pre className="max-h-64 overflow-auto rounded bg-bg-elevated p-3 text-xs">
          {JSON.stringify(out.payload, null, 2)}
        </pre>
      ) : null}
    </div>
  );
}
